import { ArrowRight, BookOpen, ChevronRight, ExternalLink, Info, Layers, MapPinned, Ruler, Search, TrendingDown, TrendingUp } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation, useParams } from 'react-router';
import { useDocumentMeta } from '../../hooks/useDocumentMeta';
import { loadLanguage } from '../../i18n';
import { Breadcrumb } from '../components/Breadcrumb';
import { META, ZONE_BY_ID, floorsShort, zoneSources, zoneText, type Zone } from './genplan/config';
import {
  HOUSING,
  MAP_PATH,
  OFFICIAL_SOURCE_URL,
  OUTSIDE_ZONES,
  SEO_ZONES,
  SITE,
  T,
  ZONE_GROUPS,
  areaChange,
  drawnArea,
  faq,
  fmtHa,
  groupOf,
  hreflangs,
  hubMeta,
  hubPath,
  seoLangFromPath,
  zoneBySlug,
  zoneMeta,
  zoneOnMapPath,
  zonePath,
  type SeoLang,
} from './genplan/seo';

/**
 * Indexable text pages for the genplan map (which itself is a canvas a crawler
 * can't read): /turaq-kg-genplan-bishkek2050/zony and …/zona/:slug, in RU and
 * KY (/ky/…). All facts come from genplan/data — the official explanatory note.
 */
export function GenplanZonesPage() {
  const { slug } = useParams();
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const lang = seoLangFromPath(pathname);

  // The URL decides the language of this page; keep the site chrome in step.
  useEffect(() => {
    const code = lang === 'ky' ? 'kg' : 'ru';
    if (i18n.language !== code) loadLanguage(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  if (slug === undefined) return <ZonesHub lang={lang} />;
  const zone = zoneBySlug(slug);
  if (!zone) return <Navigate to={hubPath(lang)} replace />;
  return <ZoneDetail zone={zone} lang={lang} />;
}

// ─── Shared bits ─────────────────────────────────────────────────────────────

const zName = (z: Zone, lang: SeoLang) => (lang === 'ky' ? z.name.ky : null) || z.name.ru;

function Swatch({ color, size = 40 }: { color: string; size?: number }) {
  return (
    <span
      aria-hidden
      className="shrink-0 rounded-xl ring-1 ring-black/10"
      style={{ width: size, height: size, background: color, boxShadow: 'inset 0 -6px 12px rgba(0,0,0,.08)' }}
    />
  );
}

function Delta({ pct }: { pct: number | null }) {
  if (pct == null) return <span className="text-slate-400">—</span>;
  const up = pct > 0;
  const Icon = pct === 0 ? null : up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 font-bold tabular-nums ${pct === 0 ? 'text-slate-500' : up ? 'text-emerald-600' : 'text-rose-600'}`}>
      {Icon && <Icon size={15} />}
      {up ? '+' : ''}
      {new Intl.NumberFormat('ru-RU').format(pct)}%
    </span>
  );
}

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <section className={`rounded-[24px] bg-white p-5 shadow-sm ring-1 ring-slate-900/5 sm:p-7 ${className}`}>{children}</section>;
}

function Hero({ eyebrow, title, lead, children }: { eyebrow: string; title: React.ReactNode; lead?: string; children?: React.ReactNode }) {
  return (
    <section className="relative overflow-hidden rounded-[28px] bg-[#0b1640] px-5 py-8 text-white sm:px-10 sm:py-12">
      <img src="/hero/bishkek-1280.webp" alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0b1640] via-[#0b1640]/90 to-indigo-900/60" />
      <div className="relative">
        <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-sky-200 ring-1 ring-white/15">
          <Layers size={13} /> {eyebrow}
        </p>
        <h1 className="mt-4 max-w-3xl text-[22px] font-black leading-tight tracking-tight sm:text-4xl">{title}</h1>
        {lead && <p className="mt-4 max-w-3xl text-[15px] leading-relaxed text-white/80 sm:text-base">{lead}</p>}
        {children}
      </div>
    </section>
  );
}

const btnPrimary =
  'inline-flex h-12 items-center justify-center gap-2 rounded-2xl bg-white px-5 text-sm font-bold text-slate-900 shadow-lg transition hover:-translate-y-px hover:bg-slate-50';
const btnGhost =
  'inline-flex h-12 items-center justify-center gap-2 rounded-2xl bg-white/10 px-5 text-sm font-bold text-white ring-1 ring-white/25 backdrop-blur transition hover:bg-white/20';

// ─── Hub: all zones ──────────────────────────────────────────────────────────

function ZonesHub({ lang }: { lang: SeoLang }) {
  const t = T[lang];
  const meta = hubMeta(lang);
  const qa = faq(lang);

  useDocumentMeta({
    title: meta.title,
    description: meta.description,
    canonical: SITE + hubPath(lang),
    hreflangAlternates: hreflangs(hubPath),
    structuredData: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: t.hubTitle,
        url: SITE + hubPath(lang),
        inLanguage: lang === 'ky' ? 'ky' : 'ru',
        about: { '@type': 'Place', name: lang === 'ky' ? 'Бишкек' : 'Бишкек', address: { '@type': 'PostalAddress', addressCountry: 'KG' } },
        isBasedOn: { '@type': 'CreativeWork', name: 'Генеральный план города Бишкек до 2050 года', url: OFFICIAL_SOURCE_URL },
        hasPart: SEO_ZONES.map((z) => ({ '@type': 'WebPage', name: zName(z, lang), url: SITE + zonePath(z, lang) })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: qa.map((x) => ({ '@type': 'Question', name: x.q, acceptedAnswer: { '@type': 'Answer', text: x.a } })),
      },
    ],
  });

  return (
    <div className="min-h-screen bg-slate-50 pb-bottom-nav md:pb-16">
      <div className="container mx-auto px-3 py-4 sm:px-4 lg:px-6 lg:py-6">
        <Breadcrumb
          items={[
            { label: t.home, to: lang === 'ky' ? '/' : '/' },
            { label: t.map, to: MAP_PATH },
            { label: t.zones },
          ]}
        />

        <Hero eyebrow={t.map} title={t.hubTitle} lead={t.hubLead}>
          <dl className="mt-6 grid max-w-2xl grid-cols-3 gap-2 sm:gap-3">
            {[
              { v: SEO_ZONES.length, l: t.statZones },
              { v: `±${META.accuracy_label_m} м`, l: t.statAccuracy },
              { v: new Intl.NumberFormat('ru-RU').format(META.accuracy_m.gcps), l: t.statPoints },
            ].map((s) => (
              <div key={s.l} className="rounded-2xl bg-white/10 px-3 py-3 ring-1 ring-white/15 backdrop-blur">
                <dt className="sr-only">{s.l}</dt>
                <dd className="text-xl font-black sm:text-2xl">{s.v}</dd>
                <dd className="mt-0.5 text-[11px] leading-tight text-white/70 sm:text-xs">{s.l}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-6 flex flex-wrap gap-2.5">
            <Link to={MAP_PATH} className={btnPrimary}>
              <MapPinned size={17} /> {t.openMap}
            </Link>
            <Link to={MAP_PATH} className={btnGhost}>
              <Search size={17} /> {t.checkPlot}
            </Link>
          </div>
        </Hero>

        <div className="mt-5 space-y-5">
          {ZONE_GROUPS.map((g) => {
            const zones = g.zones.map((id) => ZONE_BY_ID[id]).filter((z): z is Zone => !!z && SEO_ZONES.includes(z));
            if (!zones.length) return null;
            return (
              <Card key={g.id}>
                <h2 className="text-lg font-black tracking-tight text-slate-900 sm:text-xl">{t.groups[g.id]}</h2>
                <ul className="mt-4 grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
                  {zones.map((z) => {
                    const { existing, plan, pct } = areaChange(z);
                    return (
                      <li key={z.id}>
                        <Link
                          to={zonePath(z, lang)}
                          className="group flex h-full items-center gap-3.5 rounded-2xl bg-slate-50 p-3.5 ring-1 ring-slate-100 transition hover:bg-white hover:shadow-md hover:ring-blue-200"
                        >
                          <Swatch color={z.color} />
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-bold leading-snug text-slate-900 group-hover:text-blue-700">{zName(z, lang)}</span>
                            {existing != null && plan != null && (
                              <span className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
                                <span className="tabular-nums">
                                  {fmtHa(existing, lang)} → {fmtHa(plan, lang)} {t.ha}
                                </span>
                                <Delta pct={pct} />
                              </span>
                            )}
                          </span>
                          <ChevronRight size={17} className="shrink-0 text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-blue-600" />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </Card>
            );
          })}

          <Card>
            <h2 className="text-lg font-black tracking-tight text-slate-900 sm:text-xl">{t.outsideTitle}</h2>
            <p className="mt-1 text-sm text-slate-500">{t.outsideLead}</p>
            <ul className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {OUTSIDE_ZONES.map((z) => (
                <li key={z.id} className="flex items-center gap-3 rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-100">
                  <Swatch color={z.color} size={28} />
                  <span className="min-w-0 flex-1 text-sm font-semibold text-slate-700">{zName(z, lang)}</span>
                  {drawnArea(z) != null && (
                    <span className="shrink-0 text-xs tabular-nums text-slate-500">
                      ≈ {fmtHa(drawnArea(z)!, lang)} {t.ha}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </Card>

          <Card>
            <h2 className="text-lg font-black tracking-tight text-slate-900 sm:text-xl">{t.faqTitle}</h2>
            <div className="mt-3 divide-y divide-slate-100">
              {qa.map((x, i) => (
                <details key={i} className="group py-3.5" open={i === 0}>
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-[15px] font-bold text-slate-900 [&::-webkit-details-marker]:hidden">
                    {x.q}
                    <ChevronRight size={18} className="shrink-0 text-slate-400 transition group-open:rotate-90" />
                  </summary>
                  <p className="mt-2 text-[15px] leading-relaxed text-slate-600">{x.a}</p>
                </details>
              ))}
            </div>
          </Card>

          <SourceNote lang={lang} />
        </div>
      </div>
    </div>
  );
}

// ─── One zone ────────────────────────────────────────────────────────────────

function ZoneDetail({ zone: z, lang }: { zone: Zone; lang: SeoLang }) {
  const t = T[lang];
  const meta = zoneMeta(z, lang);
  const { description, includes } = zoneText(z, lang === 'ky' ? 'ky' : 'ru');
  const { existing, plan, pct } = areaChange(z);
  const drawn = drawnArea(z);
  const polys = META.zones[z.id]?.polygons;
  const floors = floorsShort(z);
  const sources = zoneSources(z, lang === 'ky' ? 'ky' : 'ru');
  const group = groupOf(z);
  const related = (group?.zones ?? []).filter((id) => id !== z.id).map((id) => ZONE_BY_ID[id]).filter((r): r is Zone => !!r && SEO_ZONES.includes(r));
  const prefix = lang === 'ky' ? '/ky' : '';

  useDocumentMeta({
    title: meta.title,
    description: meta.description,
    canonical: SITE + zonePath(z, lang),
    hreflangAlternates: hreflangs((l) => zonePath(z, l)),
    structuredData: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: meta.name,
        description: meta.description,
        url: SITE + zonePath(z, lang),
        inLanguage: lang === 'ky' ? 'ky' : 'ru',
        about: { '@type': 'Place', name: 'Бишкек', address: { '@type': 'PostalAddress', addressCountry: 'KG' } },
        isBasedOn: { '@type': 'CreativeWork', name: 'Генеральный план города Бишкек до 2050 года', url: OFFICIAL_SOURCE_URL },
        isPartOf: { '@type': 'CollectionPage', name: t.hubTitle, url: SITE + hubPath(lang) },
      },
    ],
  });

  return (
    <div className="min-h-screen bg-slate-50 pb-bottom-nav md:pb-16">
      <div className="container mx-auto px-3 py-4 sm:px-4 lg:px-6 lg:py-6">
        <Breadcrumb
          items={[
            { label: t.home, to: '/' },
            { label: t.map, to: MAP_PATH },
            { label: t.zones, to: hubPath(lang) },
            { label: meta.name },
          ]}
        />

        <Hero
          eyebrow={`${t.map}${group ? ` · ${t.groups[group.id]}` : ''}`}
          title={
            <span className="flex items-start gap-4">
              <span className="sm:hidden"><Swatch color={z.color} size={36} /></span>
              <span className="max-sm:hidden"><Swatch color={z.color} size={52} /></span>
              <span>{meta.name}</span>
            </span>
          }
        >
          <div className="mt-6 flex flex-wrap gap-2.5">
            <Link to={zoneOnMapPath(z)} className={btnPrimary}>
              <MapPinned size={17} /> {t.showOnMap}
            </Link>
            <Link to={MAP_PATH} className={btnGhost}>
              <Search size={17} /> {t.checkPlot}
            </Link>
          </div>
        </Hero>

        <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
          <div className="min-w-0 space-y-5">
            {(existing != null || drawn != null) && (
              <dl className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 sm:gap-3">
                {[
                  existing != null && { k: t.now, v: `${fmtHa(existing, lang)} ${t.ha}` },
                  plan != null && { k: t.plan, v: `${fmtHa(plan, lang)} ${t.ha}` },
                  pct != null && { k: t.change, v: <Delta pct={pct} /> },
                  drawn != null && { k: t.drawn, v: `≈ ${fmtHa(drawn, lang)} ${t.ha}` },
                ]
                  .filter(Boolean)
                  .map((s) => {
                    const item = s as { k: string; v: React.ReactNode };
                    return (
                      <div key={item.k} className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-900/5">
                        <dt className="text-xs font-medium text-slate-500">{item.k}</dt>
                        <dd className="mt-1 text-lg font-black tabular-nums text-slate-900 sm:text-xl">{item.v}</dd>
                      </div>
                    );
                  })}
              </dl>
            )}

            <Card>
              <h2 className="flex items-center gap-2 text-lg font-black tracking-tight text-slate-900 sm:text-xl">
                <BookOpen size={19} className="text-blue-600" /> {t.whatGenplanSays}
              </h2>
              {description ? (
                <p className="mt-3 whitespace-pre-line text-[15px] leading-relaxed text-slate-700 sm:text-base">{description}</p>
              ) : (
                <p className="mt-3 text-[15px] leading-relaxed text-slate-500">{t.noText}</p>
              )}
              {includes?.length ? (
                <>
                  <h3 className="mt-5 text-sm font-bold uppercase tracking-wide text-slate-500">{t.includes}</h3>
                  <ul className="mt-2 grid gap-1.5 sm:grid-cols-2">
                    {includes.map((x) => (
                      <li key={x} className="flex items-start gap-2 text-[15px] text-slate-700">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: z.color }} />
                        {x}
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
              {(floors || polys) && (
                <p className="mt-5 flex flex-wrap gap-2 text-xs">
                  {floors && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1.5 font-semibold text-slate-700">
                      <Ruler size={13} /> {t.floors}: {floors}
                    </span>
                  )}
                  {polys ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1.5 font-semibold text-slate-700">
                      <Layers size={13} /> {new Intl.NumberFormat('ru-RU').format(polys)} {t.polygons}
                    </span>
                  ) : null}
                </p>
              )}
            </Card>

            <div className="flex gap-3 rounded-[20px] bg-amber-50 p-4 ring-1 ring-amber-200 sm:p-5">
              <Info size={20} className="mt-0.5 shrink-0 text-amber-600" />
              <div>
                <p className="text-sm font-bold text-amber-900">{t.pzzTitle}</p>
                <p className="mt-1 text-sm leading-relaxed text-amber-900/80">{t.pzz}</p>
              </div>
            </div>

            {sources.length > 0 && (
              <Card>
                <h2 className="text-base font-black tracking-tight text-slate-900">{t.sources}</h2>
                <ul className="mt-2 space-y-1.5 text-sm text-slate-600">
                  {sources.map((s) => (
                    <li key={s} className="flex items-start gap-2">
                      <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-slate-400" />
                      {s}
                    </li>
                  ))}
                </ul>
                <a
                  href={OFFICIAL_SOURCE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-blue-600 hover:text-blue-800"
                >
                  {t.officialSite} <ExternalLink size={14} />
                </a>
              </Card>
            )}
          </div>

          <aside className="space-y-5">
            {related.length > 0 && (
              <Card className="!p-5">
                <h2 className="text-base font-black tracking-tight text-slate-900">{t.related}</h2>
                <ul className="mt-3 space-y-1.5">
                  {related.map((r) => (
                    <li key={r.id}>
                      <Link to={zonePath(r, lang)} className="group flex items-center gap-3 rounded-xl p-2 transition hover:bg-slate-50">
                        <Swatch color={r.color} size={26} />
                        <span className="min-w-0 flex-1 text-sm font-semibold leading-snug text-slate-700 group-hover:text-blue-700">{zName(r, lang)}</span>
                        <ChevronRight size={15} className="shrink-0 text-slate-300" />
                      </Link>
                    </li>
                  ))}
                </ul>
                <Link to={hubPath(lang)} className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-blue-600 hover:text-blue-800">
                  {t.allZones} <ArrowRight size={15} />
                </Link>
              </Card>
            )}

            {HOUSING.has(z.id) && (
              <Card className="!p-5">
                <h2 className="text-base font-black tracking-tight text-slate-900">{t.realEstate}</h2>
                <ul className="mt-3 space-y-1">
                  {[
                    { to: `${prefix}/nedvizhimost/bishkek/kvartiry`, l: t.flats },
                    { to: `${prefix}/nedvizhimost/bishkek/doma`, l: t.houses },
                    { to: `${prefix}/nedvizhimost/bishkek/uchastki`, l: t.plots },
                  ].map((x) => (
                    <li key={x.to}>
                      <Link to={x.to} className="flex items-center justify-between rounded-xl px-2 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 hover:text-blue-700">
                        {x.l} <ChevronRight size={15} className="text-slate-300" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}

function SourceNote({ lang }: { lang: SeoLang }) {
  const t = T[lang];
  return (
    <div className="flex gap-3 rounded-[20px] bg-amber-50 p-4 ring-1 ring-amber-200 sm:p-5">
      <Info size={20} className="mt-0.5 shrink-0 text-amber-600" />
      <div>
        <p className="text-sm font-bold text-amber-900">{t.pzzTitle}</p>
        <p className="mt-1 text-sm leading-relaxed text-amber-900/80">{t.pzz}</p>
        <a href={OFFICIAL_SOURCE_URL} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-sm font-bold text-amber-900 underline-offset-2 hover:underline">
          {t.officialSite} <ExternalLink size={14} />
        </a>
      </div>
    </div>
  );
}
